import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommandPaletteComponent } from './shared/command-palette/command-palette.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { BreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { BusinessGuideDrawerComponent } from './shared/business-guide-drawer/business-guide-drawer.component';
import { ThemeService } from './core/services/theme.service';
import { LoadingService } from './core/services/loading.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-root',
  imports: [
    AsyncPipe,
    RouterOutlet,
    CommandPaletteComponent,
    SidebarComponent,
    BreadcrumbComponent,
    MatProgressBarModule,
    BusinessGuideDrawerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly themeService = inject(ThemeService);
  public readonly loadingService = inject(LoadingService);

  readonly blacklistRoutes: string[] = ['login', '', 'reset-password'];
  readonly showNavbar = signal<boolean>(false);

  ngOnInit(): void {
    this.onMonitoringRouter();
  }

  onMonitoringRouter(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const firstChild = this.route.snapshot.firstChild;
        if (firstChild && firstChild.routeConfig) {
          const currentRoute = firstChild.routeConfig.path;
          if (typeof currentRoute === 'string') {
            this.showNavbar.set(
              !this.blacklistRoutes.some(
                (r) =>
                  currentRoute === r ||
                  currentRoute.startsWith(r + '/') ||
                  (r.length > 0 && currentRoute.startsWith(r))
              )
            );
          } else {
            this.showNavbar.set(true);
          }
        } else {
          this.showNavbar.set(
            !this.blacklistRoutes.some(
              (r) =>
                this.router.url === '/' ||
                this.router.url.startsWith('/' + r) ||
                (r.length > 0 && this.router.url.includes('/' + r))
            )
          );
        }
      }
    });
  }

  /** Moves focus past the sidebar. A plain "#conteudo" link would resolve against <base href> and leave the page. */
  skipToContent(event: Event): void {
    event.preventDefault();
    document.getElementById('conteudo')?.focus();
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }
}
